/**
 * Department documents for admins (D1 wave 1, workspace docs/guardian/05,
 * 2026-10-22): upload an SOP, watch it process, read the extracted text,
 * keyword-search across documents, retire. The Backend scopes everything to
 * the caller's department; text is rendered as plain text only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  createDepartmentDocument,
  fetchDepartmentDocument,
  fetchDepartmentDocumentChunks,
  fetchDepartmentDocuments,
  finalizeDepartmentDocument,
  retireDepartmentDocument,
  searchDepartmentDocuments,
  uploadToPresignedPost,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

export const MAX_BYTES = 25 * 1024 * 1024;
const POLL_MS = 3000;
const POLL_LIMIT_MS = 10 * 60 * 1000;

export const KINDS = [
  ['sop', 'SOP'],
  ['sog', 'SOG'],
  ['policy_manual', 'Policy manual'],
  ['run_card', 'Run card'],
  ['code_list', 'Code list'],
  ['roster', 'Apparatus roster'],
  ['other', 'Other'],
];

const BY_EXTENSION = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
};

/** The declared type the Backend accepts, from the file name (never guessed beyond these). */
export function mimeFor(file) {
  const ext = String(file?.name || '').toLowerCase().split('.').pop();
  return BY_EXTENSION[ext] || null;
}

export async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export const STATUS_TEXT = {
  uploading: 'Uploading',
  parsing: 'Processing',
  parsed: 'Ready',
  failed: 'Failed',
  retired: 'Retired',
};

const FAILURE_TEXT = {
  needs_ocr: 'This looks like a scanned document. Text recognition isn’t supported yet; upload a text-based PDF or Word file.',
  encrypted: 'The PDF is password-protected. Upload an unprotected copy.',
  upload_mismatch: 'The uploaded file didn’t match what was sent. Try uploading it again.',
  unsupported_type: 'This file type isn’t supported.',
  too_large: 'The file is too large.',
  too_many_pages: 'The document has more than 500 pages.',
  too_long: 'The document has too much text.',
  corrupt: 'The file couldn’t be read.',
  corrupt_result: 'Processing didn’t complete correctly. Upload it again.',
  parser_failed: 'Processing stopped unexpectedly. Upload it again.',
  timeout: 'Processing took too long. Upload it again.',
};

export function failureText(reason) {
  return FAILURE_TEXT[reason] || 'Processing failed.';
}

function pagesLabel(c) {
  if (!c.pageFrom) return '';
  return c.pageFrom === c.pageTo ? `p. ${c.pageFrom}` : `pp. ${c.pageFrom}–${c.pageTo}`;
}

function DocumentText({ authToken, doc, onClose }) {
  const [state, setState] = useState({ offset: 0, chunks: [], error: null, done: false });
  const load = useCallback(async (offset) => {
    const res = await fetchDepartmentDocumentChunks(authToken, doc.id, offset, 20);
    if (!res?.success) {
      setState((s) => ({ ...s, error: departmentErrorMessage(res, 'Could not load the text.') }));
      return;
    }
    const got = Array.isArray(res.data?.chunks) ? res.data.chunks : [];
    setState((s) => ({ offset: offset + got.length, chunks: offset === 0 ? got : [...s.chunks, ...got], error: null, done: got.length < 20 }));
  }, [authToken, doc.id]);
  useEffect(() => {
    load(0);
  }, [load]);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="doc-text-title">
      <DialogTitle id="doc-text-title">{doc.title}</DialogTitle>
      <DialogContent dividers>
        {state.error && <Alert severity="error">{state.error}</Alert>}
        {state.chunks.map((c) => (
          <Box key={c.ord} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {[c.sectionPath, pagesLabel(c)].filter(Boolean).join(' · ')}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
          </Box>
        ))}
        {!state.done && !state.error && (
          <Button onClick={() => load(state.offset)}>Show more</Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function DepartmentDocuments({ authToken }) {
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('sop');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  const load = useCallback(async () => {
    const res = await fetchDepartmentDocuments(authToken);
    if (!alive.current) return;
    if (res?.success) setDocs(Array.isArray(res.data?.documents) ? res.data.documents : []);
    else setError(departmentErrorMessage(res, 'Could not load documents.'));
  }, [authToken]);
  useEffect(() => {
    load();
  }, [load]);

  const fileError = (() => {
    if (!file) return null;
    if (!mimeFor(file)) return 'Upload a PDF, Word (.docx), text, Markdown or CSV file.';
    if (file.size > MAX_BYTES) return 'Files can be up to 25 MB.';
    if (file.size === 0) return 'This file is empty.';
    return null;
  })();

  const poll = async (id) => {
    const started = Date.now();
    while (alive.current && Date.now() - started < POLL_LIMIT_MS) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_MS));
      // eslint-disable-next-line no-await-in-loop
      const res = await fetchDepartmentDocument(authToken, id);
      if (res?.success && res.data && res.data.status !== 'parsing') return res.data;
    }
    return null;
  };

  const onUpload = async () => {
    setError(null);
    try {
      setProgress('Checking file…');
      const mime = mimeFor(file);
      const sha256 = await sha256Hex(file);
      const created = await createDepartmentDocument(authToken, {
        title: title.trim() || file.name, kind, bytes: file.size, sha256, mime,
      });
      if (!created?.success) {
        setError(departmentErrorMessage(created, 'Could not start the upload.'));
        return;
      }
      setProgress('Uploading…');
      const up = await uploadToPresignedPost(created.data.upload, file);
      if (!up.ok) {
        setError('The upload didn’t finish. Check your connection and try again.');
        return;
      }
      const id = created.data.document.id;
      const fin = await finalizeDepartmentDocument(authToken, id);
      if (!fin?.success) {
        setError(departmentErrorMessage(fin, 'Could not process the upload.'));
        return;
      }
      if (fin.data.status === 'failed') {
        setError(failureText(fin.data.failureReason));
        return;
      }
      setProgress('Processing…');
      const done = await poll(id);
      if (done?.status === 'failed') setError(failureText(done.failureReason));
      setTitle('');
      setFile(null);
    } finally {
      if (alive.current) {
        setProgress(null);
        load();
      }
    }
  };

  const onSearch = async () => {
    const res = await searchDepartmentDocuments(authToken, q.trim());
    setResults(res?.success && Array.isArray(res.data?.results) ? res.data.results : []);
    if (!res?.success) setError(departmentErrorMessage(res, 'Search failed.'));
  };

  const onRetire = async (doc) => {
    const res = await retireDepartmentDocument(authToken, doc.id);
    if (!res?.success) setError(departmentErrorMessage(res, 'Could not retire the document.'));
    load();
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Documents</Typography>
      <Typography variant="body2" color="text.secondary">
        Upload your SOPs, SOGs, policy manual or code lists. Guardian reads the text so your
        department&apos;s procedures can be reviewed and used.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 200 }} />
        <Select size="small" value={kind} onChange={(e) => setKind(e.target.value)} inputProps={{ 'aria-label': 'Document type' }}>
          {KINDS.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
        </Select>
        <Button component="label" variant="outlined" disabled={Boolean(progress)}>
          {file ? file.name : 'Choose file'}
          <input
            hidden
            type="file"
            accept=".pdf,.docx,.txt,.md,.csv"
            aria-label="Document file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Button>
        <Button variant="contained" onClick={onUpload} disabled={!file || Boolean(fileError) || Boolean(progress)}>
          {progress || 'Upload'}
        </Button>
      </Box>
      {fileError && <Typography variant="caption" color="error">{fileError}</Typography>}

      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {docs.map((d) => (
          <Box component="li" key={d.id} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2">{d.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {(KINDS.find(([v]) => v === d.kind) || [null, d.kind])[1]}
                {d.pages ? ` · ${d.pages} pages` : ''}
                {d.status === 'failed' ? ` · ${failureText(d.failureReason)}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip size="small" label={STATUS_TEXT[d.status] || d.status} color={d.status === 'parsed' ? 'success' : d.status === 'failed' ? 'error' : 'default'} />
              {d.status === 'parsed' && <Button size="small" onClick={() => setViewing(d)}>View text</Button>}
              {d.status !== 'retired' && <Button size="small" color="error" onClick={() => onRetire(d)}>Retire</Button>}
            </Box>
          </Box>
        ))}
        {docs.length === 0 && <Typography variant="body2" color="text.secondary">No documents yet.</Typography>}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField size="small" fullWidth label="Search your documents" value={q} onChange={(e) => setQ(e.target.value)} inputProps={{ maxLength: 200 }}
          onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) onSearch(); }} />
        <Button onClick={onSearch} disabled={!q.trim()}>Search</Button>
      </Box>
      {results && (
        <Box component="ul" sx={{ m: 0, pl: 2 }} aria-live="polite">
          {results.length === 0 && <Typography variant="body2" color="text.secondary">No matches.</Typography>}
          {results.map((r, i) => (
            <li key={`${r.documentId}-${i}`}>
              <Typography variant="caption" color="text.secondary">
                {[r.title, r.sectionPath, pagesLabel(r)].filter(Boolean).join(' · ')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.excerpt}</Typography>
            </li>
          ))}
        </Box>
      )}
      {viewing && <DocumentText authToken={authToken} doc={viewing} onClose={() => setViewing(null)} />}
    </Paper>
  );
}
